import {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getUsersActivities} from 'src/openapi/admin/apis/UserSearchApi';
import {useAppDispatch} from 'src/store/hooks';

import {selectSelectedColumns} from '../slices/user-search-result-columns.slice';
import {
    userSearchResultLastActiveActions,
    userSearchResultLastActiveDataSelector,
    userSearchResultLastActiveResultSelector,
} from '../slices/user-search-result-last-active.slice';
import {userSearchResultStateSelector} from '../slices/user-search-result-state.slice';
import {checkShouldLoadColumn} from '../utils';

import type {UserSearchResultCacheKeys} from '../typedef';

type Props = {
    userIds: number[],
    totalCount: number,
    columnId: string,
};

export const useGetUserSearchResultLastActive = ({userIds, totalCount, columnId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();
    const selectedColumns = useSelector(selectSelectedColumns);

    const getAction = useCallback(() => apiDispatch(getUsersActivities({
        requestBody: userIds,
    })), [userIds, apiDispatch]);

    const setLoadedData = useCallback((
        response: Record<string, Record<string, string>>,
        dataCacheKeys: UserSearchResultCacheKeys,
    ) => {
        dispatch(userSearchResultLastActiveActions.onSuccess(({
            cacheKeys: dataCacheKeys,
            totalCount,
            data: response,
        })));
    }, [totalCount, dispatch]);

    const shouldUpdateData = useMemo(
        () => checkShouldLoadColumn(columnId, selectedColumns),
        [columnId, selectedColumns],
    );

    return useCachedTable({
        dataSelector: userSearchResultLastActiveDataSelector,
        stateSelector: userSearchResultStateSelector,
        resultSelector: userSearchResultLastActiveResultSelector,
        actions: userSearchResultLastActiveActions,
        getAction,
        onSuccessLoad: setLoadedData,
        shouldClearOnUnmount: false,
        shouldUpdateData,
    });
};
